import React, { useState } from 'react';
import {
    Box,
    Typography,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Stack,
    TablePagination,
    CircularProgress,
    Backdrop
} from '@mui/material';
import { useCallback } from 'react';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import GlobalBackButton from 'views/utilities/BackButtonGlobal';
import { SearchOutlined, HomeOutlined } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { listRetailerDetailsApi } from 'utils/API/RelationManagerAPIs/RelationManagerAPiCalls';

const stateToZoneMapping = {
    'Jammu and Kashmir': 'NORTH',
    'Himachal Pradesh': 'NORTH',
    Punjab: 'NORTH',
    Chandigarh: 'NORTH',
    Haryana: 'NORTH',
    'New delhi': 'NORTH',
    'Uttar Pradesh': 'NORTH',
    Uttarakhand: 'NORTH',
    Rajasthan: 'NORTH',
    Bihar: 'EAST',
    Jharkhand: 'EAST',
    Odisha: 'EAST',
    'West Bengal': 'EAST',
    'Arunachal Pradesh': 'NORTH EAST',
    Assam: 'NORTH EAST',
    Manipur: 'NORTH EAST',
    Meghalaya: 'NORTH EAST',
    Mizoram: 'NORTH EAST',
    Nagaland: 'NORTH EAST',
    Sikkim: 'NORTH EAST',
    Tripura: 'NORTH EAST',
    Chhattisgarh: 'WEST',
    'D & N Haveli': 'WEST',
    'Daman & Diu': 'WEST',
    Goa: 'WEST',
    Gujarat: 'WEST',
    'Madhya Pradesh': 'WEST',
    Maharashtra: 'WEST',
    'Andaman and Nicobar Islands': 'SOUTH',
    'Andhra Pradesh': 'SOUTH',
    Karnataka: 'SOUTH',
    Kerala: 'SOUTH',
    Lakshadweep: 'SOUTH',
    Puducherry: 'SOUTH',
    'Tamil Nadu': 'SOUTH',
    Telangana: 'SOUTH'
};

const getZoneByState = (state) => {
    return stateToZoneMapping[state] || 'UNKNOWN';
};

const RetailerDetails = () => {
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState(dayjs());
    const [toDate, setToDate] = useState(dayjs());
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);

    const fetchData = useCallback(
        async (currentPage = page, currentLimit = rowsPerPage) => {
            setLoading(true);
            const payload = {
                from: fromDate.format('YYYY-MM-DD'),
                to: toDate.format('YYYY-MM-DD'),
                pageNumber: currentPage + 1,
                length: currentLimit
            };

            try {
                const response = await listRetailerDetailsApi(payload);
                console.log('response', response);

                if (response?.status === 200) {
                    const fetchedData = response.data.data.map((item, index) => {
                        const isB2B = !!item.business_partner_id;
                        return {
                            ...item,
                            key: index,
                            vertical: isB2B ? 'B2B' : 'B2C',
                            zone: getZoneByState(item.statename),
                            states: item.statename,
                            hierarchy: '-',
                            mobileTeam: '-',

                            // B2C Mapping
                            bcAtaCode: item.agent_code,
                            bcName: item.agent_name,
                            bcMobile: item.phone,
                            bcAddress: item.address,
                            bcDistrict: item.distname,
                            bcPincode: item.pin,
                            bcState: item.statename,
                            distributorId: item.distributors_id,
                            distributorName: item.distributors_name,
                            distributorMobile: item.distributors_phone,
                            distributorEmail: item.distributors_email,
                            distributorAddress: item.distributors_address,
                            distributorDistrict: item.distributors_district,
                            distributorPincode: item.distributors_pincode,
                            distributorState: item.distributors_state,
                            sdId: item.superdist_id,
                            sdName: item.superdistributors_name,
                            sdMobile: item.superdist_phone,
                            sdState: item.superdist_state,

                            // B2B Mapping
                            b2bPartnerId: item.business_partner_id,
                            b2bEntityName: item.business_partner_name,
                            spocName: item.spoc_name,
                            spocMobile: item.spoc_phone,
                            spocEmail: item.spoc_mail,
                            b2bAddress: isB2B ? item.address : '',
                            b2bDistrict: isB2B ? item.distname : '',
                            b2bPincode: isB2B ? item.pin : '',
                            b2bState: isB2B ? item.statename : '',
                            b2bBcAtaCode: isB2B ? item.agent_code : '',
                            b2bBcName: isB2B ? item.agent_name : '',
                            b2bBcMobile: isB2B ? item.phone : '',
                            b2bBcAddress: isB2B ? item.address : '',
                            b2bBcDistrict: isB2B ? item.distname : '',
                            b2bBcPincode: isB2B ? item.pin : '',
                            b2bBcState: isB2B ? item.statename : ''
                        };
                    });
                    console.log('fetchedData', fetchedData);
                    setData(fetchedData);
                    setTotalCount(response.data.totalCount || 0);
                } else {
                    setData([]);
                    setTotalCount(0);
                }
            } catch (error) {
                console.error('Error fetching retailer details:', error);
            } finally {
                setLoading(false);
            }
        },
        [fromDate, toDate, page, rowsPerPage]
    );

    React.useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSearch = () => {
        setPage(0);
        fetchData(0, rowsPerPage);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ p: 1 }}>
                {/* Header Section */}
                <Box sx={{ width: '100%', backgroundColor: '#fff', borderRadius: 2, mb: 1, p: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    <Typography variant="h3" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GlobalBackButton />
                        Retailer Details
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Link to="/relation-manager/dashboard" style={{ textDecoration: 'none', color: 'inherit' }}>
                            <HomeOutlined sx={{ fontSize: '20px' }} />
                        </Link>
                        <Typography component="span" sx={{ color: 'text.secondary' }}>
                            &gt;
                        </Typography>
                        <Typography component="span" sx={{ fontWeight: 500 }}>
                            Comprehensive Details
                        </Typography>
                    </Box>
                </Box>

                {/* Filter Section */}
                <Card sx={{ p: 2, mb: 2, borderRadius: '8px' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Filter by Date Range:
                        </Typography>
                        <DatePicker
                            label="From Date"
                            value={fromDate}
                            onChange={(val) => setFromDate(val)}
                            slotProps={{ textField: { size: 'small' } }}
                        />
                        <DatePicker
                            label="To Date"
                            value={toDate}
                            onChange={(val) => setToDate(val)}
                            slotProps={{ textField: { size: 'small' } }}
                        />
                        <Button variant="contained" startIcon={<SearchOutlined />} onClick={handleSearch} sx={{ height: '40px' }}>
                            Search
                        </Button>
                    </Stack>
                </Card>

                {/* Loading Backdrop */}
                <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
                    <CircularProgress color="inherit" />
                </Backdrop>

                {/* Table Section */}
                <Card sx={{ borderRadius: '8px', overflow: 'hidden' }}>
                    <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                        <Table stickyHeader aria-label="retailer details table" size="small">
                            <TableHead>
                                {/* Row 2: Columns */}
                                <TableRow>
                                    {/* Sales Vertical */}
                                    <TableCell sx={{ minWidth: 100 }}>Vertical</TableCell>
                                    <TableCell sx={{ minWidth: 100 }}>Zone</TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>States</TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>Hierarchy</TableCell>
                                    <TableCell sx={{ minWidth: 180 }}>Mobile Numbers of Acemoney Team</TableCell>

                                    {/* B2C Vertical */}
                                    <TableCell sx={{ minWidth: 160 }}>BC Merchant ATA Code</TableCell>
                                    <TableCell sx={{ minWidth: 160 }}>BC Merchant Name</TableCell>
                                    <TableCell sx={{ minWidth: 180 }}>BC Merchant Mobile Number</TableCell>
                                    <TableCell sx={{ minWidth: 200 }}>BC Merchant Address</TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>BC Merchant District</TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>BC Merchant Pin code</TableCell>
                                    <TableCell sx={{ minWidth: 120 }}>State</TableCell>
                                    <TableCell sx={{ minWidth: 130 }}>Distributor ID</TableCell>
                                    <TableCell sx={{ minWidth: 160 }}>Distributor Name</TableCell>
                                    <TableCell sx={{ minWidth: 180 }}>Distributor Mobile number</TableCell>
                                    <TableCell sx={{ minWidth: 180 }}>Distributor email Id</TableCell>
                                    <TableCell sx={{ minWidth: 200 }}>Distributor Address</TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>Distributor District</TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>Distributor pin code</TableCell>
                                    <TableCell sx={{ minWidth: 120 }}>State</TableCell>
                                    <TableCell sx={{ minWidth: 120 }}>SD-ID</TableCell>
                                    <TableCell sx={{ minWidth: 160 }}>SD Name</TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>SD Contact Number</TableCell>
                                    <TableCell sx={{ minWidth: 120 }}>SD State</TableCell>

                                    {/* B2B Vertical */}
                                    <TableCell sx={{ minWidth: 150 }}>Business Partner ID</TableCell>
                                    <TableCell sx={{ minWidth: 180 }}>Business Partner Entity Name</TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>SPOC Name</TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>SPOC Contact Number</TableCell>
                                    <TableCell sx={{ minWidth: 180 }}>SPOC-Mail ID</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.length > 0 ? (
                                    data.map((row, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>{row.vertical}</TableCell>
                                            <TableCell>{row.zone}</TableCell>
                                            <TableCell>{row.states || row.bcState}</TableCell>
                                            <TableCell>{row.hierarchy}</TableCell>
                                            <TableCell>{row.mobileTeam}</TableCell>

                                            {/* B2C */}
                                            <TableCell>{row.bcAtaCode}</TableCell>
                                            <TableCell>{row.bcName}</TableCell>
                                            <TableCell>{row.bcMobile}</TableCell>
                                            <TableCell>{row.bcAddress}</TableCell>
                                            <TableCell>{row.bcDistrict}</TableCell>
                                            <TableCell>{row.bcPincode}</TableCell>
                                            <TableCell>{row.bcState}</TableCell>
                                            <TableCell>{row.distributorId}</TableCell>
                                            <TableCell>{row.distributorName}</TableCell>
                                            <TableCell>{row.distributorMobile}</TableCell>
                                            <TableCell>{row.distributorEmail}</TableCell>
                                            <TableCell>{row.distributorAddress}</TableCell>
                                            <TableCell>{row.distributorDistrict}</TableCell>
                                            <TableCell>{row.distributorPincode}</TableCell>
                                            <TableCell>{row.distributorState}</TableCell>
                                            <TableCell>{row.sdId}</TableCell>
                                            <TableCell>{row.sdName}</TableCell>
                                            <TableCell>{row.sdMobile}</TableCell>
                                            <TableCell>{row.sdState}</TableCell>

                                            {/* B2B */}
                                            <TableCell>{row.b2bPartnerId}</TableCell>
                                            <TableCell>{row.b2bEntityName}</TableCell>
                                            <TableCell>{row.spocName}</TableCell>
                                            <TableCell>{row.spocMobile}</TableCell>
                                            <TableCell>{row.spocEmail}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={25} align="left" sx={{ py: 6, color: 'text.secondary' }}>
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                No retailer data found for the selected date range.
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5, color: 'text.disabled' }}>
                                                Try adjusting the filters and search again.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Card>
            </Box>
        </LocalizationProvider>
    );
};

export default RetailerDetails;
